'use client';

import { useTranslations } from 'next-intl';
import { FileText, Pencil, Settings2, Trash2 } from 'lucide-react';
import { Link } from '@/i18n/routing';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { cn } from '@/lib/utils';
import { PermissionGate } from '@/features/auth';
import { TemplateFieldsInfo } from './template-fields-info';
import type { OcrTemplate } from '../types';

const STATUS_STYLES: Record<string, string> = {
  active:
    'border-emerald-500/30 bg-emerald-500/10 text-emerald-600 dark:text-emerald-400',
  inactive: 'border-gray-500/30 bg-gray-500/10 text-gray-600 dark:text-gray-400'
};

interface OcrTemplateCardProps {
  template: OcrTemplate;
  onEdit: (template: OcrTemplate) => void;
  onDelete: (template: OcrTemplate) => void;
}

export function OcrTemplateCard({
  template,
  onEdit,
  onDelete
}: OcrTemplateCardProps) {
  const t = useTranslations('ocrTemplates');

  return (
    <Card className='border-border hover:border-primary/30 transition-colors'>
      <CardContent className='p-6'>
        <div className='mb-4 flex items-start justify-between gap-3'>
          <div className='flex min-w-0 items-center gap-3'>
            <div className='bg-primary/10 flex h-9 w-9 shrink-0 items-center justify-center rounded-md'>
              <FileText className='text-primary h-4 w-4' />
            </div>
            <div className='min-w-0'>
              <div className='flex items-center gap-1.5'>
                <p className='truncate font-medium'>{template.name}</p>
                <TemplateFieldsInfo templateId={template.id} />
              </div>
              {template.description && (
                <p className='text-muted-foreground truncate text-xs'>
                  {template.description}
                </p>
              )}
            </div>
          </div>
          <Badge
            variant='outline'
            className={cn(
              'shrink-0 text-xs uppercase',
              STATUS_STYLES[template.is_active ? 'active' : 'inactive']
            )}
          >
            {template.is_active ? t('statusActive') : t('statusInactive')}
          </Badge>
        </div>

        <div className='mb-4 space-y-2 text-sm'>
          <div className='flex items-center justify-between gap-2'>
            <span className='text-muted-foreground'>{t('dynamicForm')}</span>
            <span className='truncate font-medium'>
              {template.dynamic_form?.name ?? '-'}
            </span>
          </div>
          <div className='flex items-center justify-between gap-2'>
            <span className='text-muted-foreground'>{t('customFields')}</span>
            <span className='font-medium'>
              {template.custom_fields_count ?? 0}
            </span>
          </div>
          <div className='flex items-center justify-between gap-2'>
            <span className='text-muted-foreground'>{t('fieldRules')}</span>
            <span className='font-medium'>
              {template.field_rules_count ?? 0}
            </span>
          </div>
        </div>

        <div className='flex items-center justify-end gap-1 border-t pt-3'>
          <PermissionGate permissions={['ocr_templates.edit']}>
            <Button
              variant='ghost'
              size='icon'
              className='h-8 w-8'
              onClick={() => onEdit(template)}
              title={t('edit')}
            >
              <Pencil className='h-4 w-4' />
            </Button>
          </PermissionGate>
          <PermissionGate permissions={['ocr_templates.edit']}>
            <Button
              asChild
              variant='ghost'
              size='icon'
              className='h-8 w-8'
              title={t('configure')}
            >
              <Link href={`/dashboard/ocr-templates/${template.id}/configure`}>
                <Settings2 className='h-4 w-4' />
              </Link>
            </Button>
          </PermissionGate>
          <PermissionGate permissions={['ocr_templates.delete']}>
            <Button
              variant='ghost'
              size='icon'
              className='h-8 w-8'
              onClick={() => onDelete(template)}
              title={t('delete')}
            >
              <Trash2 className='text-destructive h-4 w-4' />
            </Button>
          </PermissionGate>
        </div>
      </CardContent>
    </Card>
  );
}
